import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";

import BookingCard, { formatAmount } from "../bookingCard/BookingCard";
import { GET_TRIP_PRICING, getTripPricing } from "./RecapFormsAPI";
import { usePublicationTracking } from "../../../../tracking";

const TravelerTypeEnum = Object.freeze({
  ADULT: "ADULT",
  CHILD: "CHILD",
  BABY: "BABY",
});

const asString = (v) => (v === null || v === undefined ? "" : String(v));

// Délai avant de recalculer le prix : évite un appel réseau par clic quand on
// ajuste rapidement le nombre de voyageurs (sans retarder l'indicateur de
// chargement lui-même, affiché immédiatement).
const PRICING_DEBOUNCE_MS = 400;

const toAmount = (p) => (p ? { value: p.value ?? p.amount, currency: p.currency ?? "" } : undefined);

/**
 * Réservation du détail d'un voyage : dates de départ, voyageurs, prix
 * calculé par l'API (MT_GET_TRIP_PRICING) et passage au tunnel /booking.
 * L'affichage est délégué à BookingCard.
 */
const RecapForms = ({
  tripPublicationId,
  tripDateId,
  nbAdults,
  nbChilds,
  nbBabies,
  acquisitions,
  setIndividualPrice,
  tripFlight,
  setStayNbNights,
  setStayNbDays,
  onTripDateChange,
  navigate,
  publicationId,
  establishmentId,
}) => {
  const { t } = useTranslation();
  const [getTripPricingMutation] = useMutation(GET_TRIP_PRICING);

  const [travelers, setTravelers] = useState({
    adults: Number(nbAdults) > 0 ? Number(nbAdults) : 1,
    children: Number(nbChilds) || 0,
    babies: Number(nbBabies) || 0,
  });
  const [tripDate, setTripDate] = useState(null);
  const [tripPricing, setTripPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  // "error" : échec du calcul ; "auth" : l'API demande une connexion (401).
  const [pricingIssue, setPricingIssue] = useState(null);

  const { markBookingNavigation } = usePublicationTracking({
    publicationId,
    establishmentId,
    enabled: Boolean(publicationId && establishmentId),
    trackView: false,
    trackTimeSpent: false,
  });

  // Réservation sans vol uniquement (réservation de vols désactivée).
  const eligibleTripFlights = useMemo(
    () =>
      (tripFlight?.content ?? []).filter(
        (item) =>
          (item?.isFlightIncluded === false || item?.isFlightIncluded === null) &&
          !item?.departureInfo
      ),
    [tripFlight]
  );

  const tripDates = useMemo(
    () => eligibleTripFlights.flatMap((item) => item?.tripDateSummary ?? []).filter((d) => d?.id),
    [eligibleTripFlights]
  );

  // Âges limites de la publication, pour les indications sous Enfants / Bébés.
  const childsMaxAge = eligibleTripFlights[0]?.childsMaxAge;
  const babiesMaxAge = eligibleTripFlights[0]?.babiesMaxAge;

  // Date de départ : celle de l'URL, sinon la première disponible.
  useEffect(() => {
    const fromUrl = tripDates.find((d) => asString(d.id) === asString(tripDateId));
    setTripDate(fromUrl || tripDates[0] || null);
  }, [tripDates, tripDateId]);

  useEffect(() => {
    onTripDateChange?.(tripDate);
    setIndividualPrice?.(tripDate?.adultPricePerPerson);
    setStayNbDays?.(tripDate?.nbDays);
    setStayNbNights?.(tripDate?.nbNight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDate]);

  const mapClientList = () => {
    const createClients = (count, ageCategory) =>
      Array.from({ length: count }, (_, i) => ({
        ageCategory,
        isMainPerson: i === 0 && ageCategory === TravelerTypeEnum.ADULT,
      }));

    return [
      ...createClients(travelers.adults, TravelerTypeEnum.ADULT),
      ...createClients(travelers.children, TravelerTypeEnum.CHILD),
      ...createClients(travelers.babies, TravelerTypeEnum.BABY),
    ];
  };

  const mapPurchased = (group) =>
    group
      ? Object.entries(group).flatMap(([id, counts]) =>
          [
            counts.adult ? { id, ageCategory: TravelerTypeEnum.ADULT, count: counts.adult } : null,
            counts.child ? { id, ageCategory: TravelerTypeEnum.CHILD, count: counts.child } : null,
          ].filter(Boolean)
        )
      : [];

  // Nouveau calcul du prix à chaque changement de date, de voyageurs ou d'options.
  useEffect(() => {
    if (!tripDate?.id) {
      setTripPricing(null);
      return undefined;
    }
    let cancelled = false;
    setPricingLoading(true);
    setPricingIssue(null);

    const timer = setTimeout(() => {
      getTripPricing(getTripPricingMutation, {
        publicationId: tripPublicationId,
        tripDateId: tripDate.id,
        activitesPurchased: mapPurchased(acquisitions?.activity),
        optionsPurchased: mapPurchased(acquisitions?.option),
        clients: mapClientList(),
      })
        .then(({ data }) => {
          if (cancelled) return;
          const result = data?.MT_GET_TRIP_PRICING;
          if (result?.data) setTripPricing(result.data);
          else {
            setTripPricing(null);
            setPricingIssue(Number(result?.code) === 401 ? "auth" : "error");
          }
        })
        .catch(() => {
          if (!cancelled) {
            setTripPricing(null);
            setPricingIssue("error");
          }
        })
        .finally(() => {
          if (!cancelled) setPricingLoading(false);
        });
    }, PRICING_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDate, acquisitions, travelers.adults, travelers.children, travelers.babies]);

  const handleBookingClick = () => {
    if (!tripDate?.id) return;
    markBookingNavigation();

    const search = new URLSearchParams({
      nbAdults: String(travelers.adults),
      nbChilds: String(travelers.children),
      nbBabies: String(travelers.babies),
    }).toString();

    navigate(`/trips/${tripPublicationId}/${tripDate.id}/booking?${search}`, {
      state: {
        acquisitions: acquisitions || {},
        selectedTripDate: tripDate,
      },
    });
  };

  /* ---------------------------------------------------------- Montants */
  const paying = travelers.adults + travelers.children + travelers.babies;
  const perPerson = toAmount(tripPricing?.travelPricingPerPerson);
  const summary = tripPricing
    ? [
        {
          label: `${paying} × ${formatAmount(perPerson) ?? "—"}`,
          amount: toAmount(tripPricing.travelTotalPriceWithoutCharge),
        },
        { label: t("Reservation.ServiceFees", "Frais de service"), amount: toAmount(tripPricing.serviceFees) },
        tripPricing.optionsTotalPrice?.value
          ? { label: t("Reservation.OptionsPurchased", "Options"), amount: toAmount(tripPricing.optionsTotalPrice) }
          : null,
        tripPricing.activitiesTotalPrice?.value
          ? { label: t("Reservation.ActivitesPurchased", "Activités"), amount: toAmount(tripPricing.activitiesTotalPrice) }
          : null,
      ].filter(Boolean)
    : [];

  const age = (key, fallback, max) =>
    Number.isFinite(Number(max)) && max !== null
      ? t(key, { max, defaultValue: fallback.replace("{{max}}", max) })
      : undefined;

  const message = !tripDates.length
    ? t("tripDetails.booking.noDates", "Aucune date de départ disponible pour le moment.")
    : pricingIssue === "auth"
    ? t("tripDetails.booking.pricingAuth", "Connectez-vous pour afficher le prix total de votre voyage.")
    : pricingIssue === "error"
    ? t("tripDetails.booking.pricingError", "Le prix n'a pas pu être calculé. Réessayez dans un instant.")
    : t("Reservation.BookingMessage", "Aucun montant ne vous sera débité pour le moment.");

  return (
    <BookingCard
      price={toAmount(tripDate?.adultPricePerPerson)}
      dates={tripDates.map((d) => ({
        id: d.id,
        startDate: d.startDate,
        endDate: d.endDate,
        price: toAmount(d.adultPricePerPerson),
      }))}
      selectedDateId={tripDate?.id}
      onSelectDate={(id) => setTripDate(tripDates.find((d) => asString(d.id) === asString(id)) || null)}
      travelers={travelers}
      onTravelersChange={setTravelers}
      travelerHints={{
        children: age("tripDetails.booking.childrenHint", "Jusqu'à {{max}} ans", childsMaxAge),
        babies: age("tripDetails.booking.babiesHint", "Moins de {{max}} ans", babiesMaxAge),
      }}
      summary={summary}
      total={tripPricing ? toAmount(tripPricing.travelTotalPrice) : tripDate && !pricingIssue ? { value: null } : undefined}
      loading={pricingLoading}
      ctaLabel={t("tripDetails.booking.reserve", "Réserver ce voyage")}
      onReserve={handleBookingClick}
      reserveDisabled={!tripDate}
      message={message}
      security={t("tripDetails.booking.security", "Paiement sécurisé · Assistance avant et pendant le voyage")}
      t={t}
    />
  );
};

RecapForms.propTypes = {
  tripPublicationId: PropTypes.string.isRequired,
  tripDateId: PropTypes.string,
  nbAdults: PropTypes.number,
  nbChilds: PropTypes.number,
  nbBabies: PropTypes.number,
  acquisitions: PropTypes.object,
  tripFlight: PropTypes.object.isRequired,
  setIndividualPrice: PropTypes.func,
  setStayNbNights: PropTypes.func,
  setStayNbDays: PropTypes.func,
  onTripDateChange: PropTypes.func,
  navigate: PropTypes.func.isRequired,
  publicationId: PropTypes.string,
  establishmentId: PropTypes.string,
};

export default RecapForms;
